import { color as colorVar, cx, fontSize, fontWeight, lineHeight, type ColorToken, type FontToken, type WeightToken } from "./tokens.ts";

export type TextProps = {
  variant?: FontToken;
  weight?: WeightToken;
  color?: ColorToken;
  lines?: number;
  align?: "start" | "center" | "end";
  selectable?: boolean;
  /** Monospaced (code blocks, ids). */
  mono?: boolean;
  /** Struck through (a cancelled event, a time that changed). */
  strike?: boolean;
  /**
   * A heading of this level for assistive tech (page and screen titles = 1, sections = 2…). Web renders `<h1>`–`<h4>`
   * with the same look; iOS `.accessibilityAddTraits(.isHeader)` + `.accessibilityHeading`, Android `semantics { heading() }`.
   */
  heading?: 1 | 2 | 3 | 4;
  testID?: string;
  children: string;
};

const headingTags = { 1: "h1", 2: "h2", 3: "h3", 4: "h4" } as const;

export function Text({ variant = "body", weight, color = "text.primary", lines, align, selectable, mono, strike, heading, testID, children }: TextProps) {
  const Tag = heading === undefined ? "span" : headingTags[heading];
  return (
    <Tag
      className={cx("b-text", heading !== undefined && "b-heading", lines !== undefined && "b-clamp", selectable === true && "b-selectable", mono === true && "b-mono", strike === true && "b-strike")}
      data-testid={testID}
      style={{
        fontSize: fontSize(variant),
        lineHeight: lineHeight(variant),
        fontWeight: weight === undefined ? (variant === "headline" ? fontWeight("semibold") : undefined) : fontWeight(weight),
        color: colorVar(color),
        WebkitLineClamp: lines,
        textAlign: align === undefined ? undefined : align === "start" ? "left" : align === "end" ? "right" : "center",
      }}
    >
      {children}
    </Tag>
  );
}
